<template>
  <Dialog
    :visible.sync="dialogVisible"
    width="900px"
    :fullscreen="isFullscreen"
    :close-on-click-modal="false"
    :before-close="closeDialog"
    custom-class="resource-preview-dialog"
    append-to-body
    @closed="handleClosed"
  >
    <div slot="title" class="preview-dialog-title">
      <span class="preview-dialog-title__text">{{ title }}</span>
      <div class="preview-dialog-title__actions">
        <el-button
          type="text"
          class="preview-dialog-title__action"
          :title="isFullscreen ? '退出全屏' : '全屏'"
          :icon="isFullscreen ? 'el-icon-crop' : 'el-icon-full-screen'"
          @click.stop="toggleFullscreen"
        />
      </div>
    </div>

    <div
      class="resource-preview-wrap"
      :class="{ 'resource-preview-wrap--fullscreen': isFullscreen }"
      v-loading="loading"
    >
      <video
        v-if="previewType === 'video' && fileUrl"
        ref="videoRef"
        class="preview-media"
        :src="fileUrl"
        controls
        controlslist="nodownload"
        preload="metadata"
      >
        您的浏览器不支持 video 标签。
      </video>

      <audio
        v-else-if="previewType === 'audio' && fileUrl"
        ref="audioRef"
        class="preview-audio"
        :src="fileUrl"
        controls
        preload="metadata"
      >
        您的浏览器不支持 audio 标签。
      </audio>

      <vue-office-pdf
        v-else-if="previewType === 'pdf' && previewSrc"
        :key="'pdf-' + previewKey"
        class="preview-office"
        :src="previewSrc"
        :static-file-url="pdfStaticUrl"
        @error="handlePreviewError"
      />

      <vue-office-docx
        v-else-if="previewType === 'word' && previewSrc"
        :key="'docx-' + previewKey"
        class="preview-office"
        :src="previewSrc"
        @error="handlePreviewError"
      />

      <vue-office-excel
        v-else-if="previewType === 'excel' && previewSrc"
        :key="'excel-' + previewKey"
        class="preview-office"
        :src="previewSrc"
        @error="handlePreviewError"
      />

      <div
        v-else-if="previewLoadError || legacyFormat"
        class="preview-fallback"
      >
        <p v-if="legacyFormat">
          暂不支持旧版 .doc / .xls 在线预览，请下载后查看
        </p>
        <p v-else>文件加载失败，请检查文件地址或跨域配置</p>
        <el-button plain round size="small" type="primary" @click="openInNewTab"
          >新窗口打开</el-button
        >
        <el-button plain round size="small" @click="downloadFile"
          >下载文件</el-button
        >
      </div>

      <div v-else-if="fileUrl && needFetchPreview" class="preview-empty">
        正在加载预览...
      </div>

      <div v-else-if="fileUrl" class="preview-fallback">
        <p>当前文件类型暂不支持在线预览</p>
        <el-button plain round size="small" type="primary" @click="openInNewTab"
          >新窗口打开</el-button
        >
        <el-button plain round size="small" @click="downloadFile"
          >下载文件</el-button
        >
      </div>

      <div v-else class="preview-empty">暂无文件地址</div>
    </div>
  </Dialog>
</template>

<script>
import "@vue-office/docx/lib/index.css";
import "@vue-office/excel/lib/index.css";
import {
  resolvePreviewType,
  fetchPreviewFile,
  isLegacyOfficeFormat
} from "@/utils/resourcePreview";

const VueOfficeDocx = require("@vue-office/docx");
const VueOfficeExcel = require("@vue-office/excel");
const VueOfficePdf = require("@vue-office/pdf");

const OFFICE_PREVIEW_TYPES = ["pdf", "word", "excel"];
const PDFJS_STATIC_URL = "https://unpkg.com/pdfjs-dist@3.1.81/";

export default {
  name: "ResourcePreviewDialog",
  components: {
    VueOfficeDocx,
    VueOfficeExcel,
    VueOfficePdf
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      fileUrl: "",
      previewSrc: null,
      previewKey: 0,
      previewLoadError: false,
      legacyFormat: false,
      title: "资源预览",
      previewType: "",
      resourceType: "",
      resourceTypeList: [],
      pdfStaticUrl: PDFJS_STATIC_URL,
      isFullscreen: false
    };
  },
  computed: {
    needFetchPreview() {
      return OFFICE_PREVIEW_TYPES.includes(this.previewType);
    }
  },
  methods: {
    async open(payload = {}) {
      const options =
        typeof payload === "string"
          ? { url: payload }
          : Object.assign({}, payload);

      const url = options.url || options.urls || options.resourceUrl || "";
      if (!url) {
        this.$message.warning("暂无文件地址");
        return;
      }

      this.fileUrl = url;
      this.title = options.name || options.resourceName || "资源预览";
      this.resourceType = options.resourceType;
      this.resourceTypeList = options.resourceTypeList || [];
      this.previewType = resolvePreviewType({
        url,
        resourceType: options.resourceType,
        resourceTypeList: this.resourceTypeList
      });
      this.previewSrc = null;
      this.previewLoadError = false;
      this.legacyFormat = isLegacyOfficeFormat(url);
      this.previewKey += 1;
      this.isFullscreen = false;
      this.dialogVisible = true;

      if (this.needFetchPreview && !this.legacyFormat) {
        await this.loadPreviewFile(url);
      }
    },
    async loadPreviewFile(url) {
      this.loading = true;
      this.previewLoadError = false;
      try {
        this.previewSrc = await fetchPreviewFile(url);
      } catch (e) {
        // 降级：vue-office 也支持直接传 URL
        this.previewSrc = url;
      } finally {
        this.loading = false;
      }
    },
    handlePreviewError() {
      this.previewLoadError = true;
      this.previewSrc = null;
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      // excel 按挂载时容器尺寸渲染，全屏后需重挂载才能撑满
      if (this.previewType === "excel" && this.previewSrc) {
        this.$nextTick(() => {
          this.previewKey += 1;
        });
      }
    },
    closeDialog(done) {
      this.stopMedia();
      this.isFullscreen = false;
      this.dialogVisible = false;
      done && done();
    },
    handleClosed() {
      this.stopMedia();
      this.isFullscreen = false;
      this.fileUrl = "";
      this.previewSrc = null;
      this.previewLoadError = false;
      this.legacyFormat = false;
      this.title = "资源预览";
      this.previewType = "";
      this.resourceType = "";
      this.resourceTypeList = [];
    },
    stopMedia() {
      const video = this.$refs.videoRef;
      const audio = this.$refs.audioRef;
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    },
    openInNewTab() {
      window.open(this.fileUrl, "_blank");
    },
    async downloadFile() {
      if (!this.fileUrl) {
        this.$message.warning("暂无文件地址");
        return;
      }
      const fileName = this.title || "课件";
      try {
        const res = await fetch(this.fileUrl, { mode: "cors" });
        if (!res.ok) {
          throw new Error("download failed");
        }
        const blob = await res.blob();
        const href = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = href;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(href);
      } catch (e) {
        this.$message.error("下载失败，请检查文件地址或跨域配置");
      }
    }
  }
};
</script>

<style scoped lang="less">
.preview-dialog-title {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 28px;
  width: 100%;
  min-width: 0;
}

.preview-dialog-title__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #1f2a37;
  max-width: 100%;
}

.preview-dialog-title__actions {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
}

.preview-dialog-title__action {
  padding: 0 4px;
  font-size: 16px;
  color: #909399;

  &:hover {
    color: #409eff;
  }
}

.resource-preview-wrap {
  min-height: 480px;
  background: #f5f7fa;
  border-radius: 4px;
  overflow: hidden;
}

.resource-preview-wrap--fullscreen {
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.resource-preview-wrap--fullscreen .preview-media,
.resource-preview-wrap--fullscreen .preview-office {
  max-height: none;
  flex: 1;
  min-height: 0;
  height: 100%;
}

.resource-preview-wrap--fullscreen .preview-audio {
  margin: 0;
  align-self: center;
  max-width: 800px;
  flex: none;
}

.preview-media {
  display: block;
  width: 100%;
  max-height: 520px;
  background: #000;
  outline: none;
}

.preview-audio {
  display: block;
  width: 100%;
  margin: 200px auto 0;
  outline: none;
}

.preview-office {
  width: 100%;
  height: 520px;
  overflow: auto;
  background: #fff;
}

.preview-fallback,
.preview-empty {
  height: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  gap: 12px;
}

.resource-preview-wrap--fullscreen .preview-fallback,
.resource-preview-wrap--fullscreen .preview-empty {
  flex: 1;
  height: 100%;
}
</style>

<style lang="less">
.resource-preview-dialog {
  &.app-dialog .el-dialog__header {
    padding: 16px 24px 12px;
  }

  .app-dialog__title-wrap {
    padding-right: 56px;
  }

  .preview-dialog-title__actions {
    right: 0;
  }
}

.resource-preview-dialog.is-fullscreen {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border-radius: 0 !important;

  .el-dialog__body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding-bottom: 16px;
  }

  .app-dialog__body {
    flex: 1;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ResourcePreviewDialog.vue